import React, { useEffect, useState } from 'react';
import {
  Avatar, Box, Flex, FlexProps, HStack, IconButton, Image, Menu, MenuButton, MenuDivider,
  MenuGroup, MenuItem, MenuList, Text, VStack, useColorModeValue,
} from '@chakra-ui/react';
import {
  FiChevronDown, FiEyeOff, FiMenu, FiSearch, FiSliders, FiExternalLink,
} from 'react-icons/fi';
import { useAuth0 } from '@auth0/auth0-react';
import { useHistory } from 'react-router-dom';
import { getLaunchSet, launchEnvironment } from '../launchEnvironment';
import useCurrentUser from '../../hooks/useCurrentUser';
import useEffectiveBrand from '../../hooks/useEffectiveBrand';
import { useAppearance } from '../../theme/AppearanceProvider';
import {
  getImpersonatedRoleName, getImpersonatedUserId, IMPERSONATABLE_ROLES, startImpersonation, stopImpersonation,
} from '../../utils/impersonation';
import { OPEN_SEARCH_EVENT } from '../CommandPalette';
import HeaderLogo from '../../assets/BasixBooks-Header.png';
import trimTransparentEdges from '../logoTrim';
import { glowColorFor } from '../../theme/brandTheme';
import AuroraMenuButton from '../AuroraMenuButton';
import ImpersonateUserGroup from '../ImpersonateUserGroup'; // item 851
import TimerChip from '../TimerChip'; // item 1337 — the work-order timer
import { TOPBAR_HEIGHT, displayUserName } from '../SidebarWithHeader';

// item 322f (cut 2) — the mobile-only top bar (hamburger + logo + avatar menu),
// extracted from the SidebarWithHeader monolith.
interface TopBarProps extends FlexProps {
  onOpen: () => void;
}
// mobile-only strip: hamburger + logo; desktop has no top bar at all —
// the sidebar is the chrome (item 46 final structure)
const TopBar = ({ onOpen, ...rest }: TopBarProps) => {
  const history = useHistory();
  const { user, logout } = useAuth0();
  const { brand } = useEffectiveBrand();
  const {
    isTeam, isOutsideSales, canImpersonate, user: currentUser,
  } = useCurrentUser(); // item 34/272 — hide user mgmt from Team; hide settings/users from external reps
  const { stealth, setStealth } = useAppearance(); // item 327

  // trim transparent padding at render time too, so logos stored before the
  // upload-time trim existed still display at their true size
  const [trimmedLogo, setTrimmedLogo] = useState<string | undefined>(undefined);
  useEffect(() => {
    let alive = true;
    if (brand?.logoDataUrl) {
      trimTransparentEdges(brand.logoDataUrl).then((t) => {
        if (alive) setTrimmedLogo(t);
      });
    } else {
      setTrimmedLogo(undefined);
    }
    return () => { alive = false; };
  }, [brand?.logoDataUrl]);
  const logoSrc = trimmedLogo ?? brand?.logoDataUrl;
  // never stretch a logo beyond 2x its real pixels — that's where grain
  // comes from; small files render smaller-but-crisp
  const [logoNaturalH, setLogoNaturalH] = useState<number | undefined>(undefined);
  const logoH = logoNaturalH ? `${Math.min(48, Math.max(28, logoNaturalH * 2))}px` : '48px';

  // neutral glass shell (item 46 direction): the bar is never painted with
  // the brand — the brand lives as the logo and the glow edge below the bar
  const onPrimary = useColorModeValue('gray.900', 'white');
  const logoFilter = useColorModeValue('brightness(0)', 'brightness(0) invert(1)');
  const barBg = useColorModeValue('rgba(255, 255, 255, 0.85)', 'rgba(11, 14, 19, 0.8)');
  const glow = glowColorFor(brand);

  return (
    <Flex
      display={{ base: 'flex', lg: 'none' }}
      px={4}
      height={TOPBAR_HEIGHT}
      w="full"
      pos="fixed"
      zIndex={100}
      alignItems="center"
      bg={barBg}
      sx={{ backdropFilter: 'var(--bx-glass-panel)' }}
      boxShadow="0 6px 24px rgba(0,0,0,0.25)"
      justifyContent="space-between"
      {...rest}
    >
      <Box
        position="absolute"
        bottom={0}
        left={0}
        right={0}
        h="1px"
        bgGradient={`linear(to-r, transparent, ${glow}, transparent)`}
        boxShadow={`0 0 12px ${glow}`}
      />
      <HStack spacing={4} minW={0}>
        <AuroraMenuButton onClick={onOpen} display={{ base: 'flex', lg: 'none' }} />
      </HStack>

      {/* item 1091 (Chad: "kill search in lieu of centering the logo?") — neither dies:
          the logo is ABSOLUTELY centered in the bar, independent of what flanks it
          (hamburger left, search + avatar right). maxW keeps it clear of both sides. */}
      <Flex
        position="absolute"
        left="50%"
        transform="translateX(-50%)"
        align="center"
        justify="center"
        maxW="calc(100% - 190px)"
        pointerEvents="none"
      >
        {brand && logoSrc
        && (
          <Image
            display="flex"
            src={logoSrc}
            h={logoH}
            maxW="100%"
            objectFit="contain"
            filter={logoFilter}
            onLoad={(e) => setLogoNaturalH(e.currentTarget.naturalHeight)}
          />
        )}
        {brand && !logoSrc
        && (
          brand.companyName
            ? (
              <Text fontSize="xl" fontWeight="bold" color={onPrimary} whiteSpace="nowrap">
                {brand.companyName}
              </Text>
            )
            : <Image display="flex" src={HeaderLogo} h="40px" objectFit="contain" filter={logoFilter} />
        )}
      </Flex>

      <Text
        color={useColorModeValue('blackAlpha.600', 'whiteAlpha.600')}
        fontWeight="medium"
        letterSpacing="0.18em"
        fontSize="xs"
        display={{ base: 'none', lg: 'block' }}
        textAlign="center"
      >
        LET&apos;S MAKE BUSINESS EASIER
      </Text>

      <HStack spacing={{ base: '0', lg: '6' }}>
        {/* item 1337 — start/stop timer + the unbooked-time nag */}
        <TimerChip />
        <IconButton
          aria-label="Search"
          icon={<FiSearch />}
          variant="ghost"
          color={onPrimary}
          mr={2}
          display={{ base: 'flex', lg: 'none' }}
          onClick={() => window.dispatchEvent(new Event(OPEN_SEARCH_EVENT))}
        />
        <Flex alignItems="center">
          <Menu>
            <MenuButton
              py={2}
              transition="all 0.3s"
              sx={{ '&:focus': { boxShadow: 'none' }, '&:focus-visible': { boxShadow: 'outline' } }}
            >
              <HStack>
                <Avatar
                  size="sm"
                  src={user?.picture}
                />
                <VStack
                  display={{ base: 'none', lg: 'flex' }}
                  alignItems="flex-start"
                  spacing="1px"
                  ml="2"
                >
                  <Text fontSize="sm" color={onPrimary} noOfLines={1}>{displayUserName(user?.name)}</Text>
                  <Text fontSize="xs" color={onPrimary}>
                    Admin
                  </Text>
                </VStack>
                <Box display={{ base: 'none', lg: 'flex' }} color={onPrimary}>
                  <FiChevronDown />
                </Box>
              </HStack>
            </MenuButton>
            <MenuList>
              {/* item 320 — ALL personal settings (incl. profile photo) live on the user's
                  own Edit User Info page — one place, not scattered menu items */}
              {currentUser?.id && (
                <MenuItem onClick={() => history.push(`/user/${currentUser.id}`)}>My profile</MenuItem>
              )}
              {/* item 500 — personal, per-device options (moved out of TOOLS) */}
              <MenuItem icon={<FiSliders />} onClick={() => history.push('/appearance')}>Appearance</MenuItem>
              <MenuItem
                icon={<FiExternalLink />}
                onClick={() => {
                  const set = getLaunchSet();
                  if (set.length === 0) history.push('/settings/');
                  else launchEnvironment(set);
                }}
              >
                My Environment
              </MenuItem>
              {!isOutsideSales && <MenuItem onClick={() => history.push('/settings/')}>Settings</MenuItem>}
              {!isTeam && !isOutsideSales && <MenuItem onClick={() => history.push('/users/')}>Users</MenuItem>}
              {/* item 327 — one tap hides every number app-wide (client demos) */}
              <MenuItem icon={<FiEyeOff />} onClick={() => setStealth(!stealth)}>
                {stealth ? 'Stealth mode: ON — show numbers' : 'Stealth mode — hide numbers'}
              </MenuItem>
              {/* item 323 — super-admin testing tool: view + act as another role */}
              {canImpersonate && !getImpersonatedRoleName() && (
                <>
                  <MenuDivider />
                  <MenuGroup title="Impersonate role (testing)">
                    {IMPERSONATABLE_ROLES.map((r) => (
                      <MenuItem key={r.value} onClick={() => startImpersonation(r.value)}>
                        {r.label}
                      </MenuItem>
                    ))}
                  </MenuGroup>
                  <ImpersonateUserGroup />
                </>
              )}
              {(getImpersonatedRoleName() || getImpersonatedUserId()) && (
                <MenuItem color="orange.400" onClick={stopImpersonation}>Exit impersonation</MenuItem>
              )}
              <MenuDivider />
              <MenuItem onClick={() => logout({ returnTo: window.location.origin })}>Sign out</MenuItem>
            </MenuList>
          </Menu>
        </Flex>
      </HStack>
    </Flex>
  );
};

export default TopBar;
